import { useEffect, useState } from 'react';
import type { EventVendorRecipient, UploadLinkConfig } from '@peles/shared';
import { ComposeEmailModal } from '../messaging/ComposeEmailModal';
import { UploadLinkOptions } from '../messaging/UploadLinkOptions';
import { useSendVendorEmail } from '../../hooks/useEvents';

export interface ComposeVendorEmailModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Selected vendors — all must already have a resolved email. */
  recipients: EventVendorRecipient[];
}

/**
 * Compose an email to the selected event vendors. A thin wrapper over the
 * generic {@link ComposeEmailModal} that adds vendor-specific behaviour: the
 * recipients come from the vendor selection, and an optional per-vendor
 * "upload a file" link can be appended to each email.
 */
export function ComposeVendorEmailModal({
  open,
  onClose,
  eventId,
  recipients,
}: ComposeVendorEmailModalProps) {
  const [includeUploadLink, setIncludeUploadLink] = useState(false);
  const [uploadConfig, setUploadConfig] = useState<UploadLinkConfig>({});
  const sendEmail = useSendVendorEmail(eventId);

  useEffect(() => {
    if (open) {
      setIncludeUploadLink(false);
      setUploadConfig({});
    }
  }, [open]);

  return (
    <ComposeEmailModal
      open={open}
      onClose={onClose}
      title="Email Vendors"
      fixedRecipients={recipients.map((r) => ({
        id: r.vendorId,
        label: r.businessName,
        email: r.email,
      }))}
      allowManualRecipients
      eventIdForDocuments={eventId}
      extraOptions={
        <UploadLinkOptions
          enabled={includeUploadLink}
          onEnabledChange={setIncludeUploadLink}
          config={uploadConfig}
          onConfigChange={setUploadConfig}
          // A vendor email always has a record to attach to — the vendor on
          // this event — so a folder only says where it is *also* filed.
          hasRecordTarget
          // Sent from an event, so the folders offered are that event's. The
          // whole library was the old answer, and it put a hundred other
          // clients' folders one keystroke from the right one.
          scope={{ entityType: 'EVENT', entityId: eventId }}
        />
      }
      sendLabel={(count) => `Send to ${count} recipient${count === 1 ? '' : 's'}`}
      onSend={async (payload) => {
        const result = await sendEmail.mutateAsync({
          vendorIds: recipients.map((r) => r.vendorId),
          subject: payload.subject,
          bodyHtml: payload.bodyHtml,
          attachments: payload.attachments,
          // Forwarded, not dropped. Without this a file picked from the library
          // vanishes silently on the one screen the feature was asked for.
          fileIds: payload.fileIds,
          ccSelf: payload.ccSelf,
          additionalEmails: payload.additionalEmails,
          includeUploadLink,
          ...(includeUploadLink ? { uploadRequest: uploadConfig } : {}),
        });
        return {
          sentCount: result.sent.length,
          skipped: result.skipped.map((skip) => ({
            label:
              recipients.find((r) => r.vendorId === skip.vendorId)?.businessName ?? skip.vendorId,
            reason: skip.reason,
          })),
        };
      }}
    />
  );
}
